import { z } from 'zod'

/**
 * Tundra-readiness / cable-crossing forecast chart contract.
 *
 * Consumer-side mirror of the ModelFactory chart payload built by
 * `modelfactory/services/chart_payload.py` (authoritative contract:
 * ModelFactory `docs/models/tundra-readiness-local.md`). Rows are
 * Santos-shaped (Historical = the model's observed daily 30 cm series --
 * the chart-parity series; Forecast = the predicted 30 cm trajectory
 * quantiles). Meta carries the machine-readable status fields the frontend
 * MUST gate rendering on, plus the compulsory disclaimers it MUST display.
 */

export const tundraForecastRow = z.object({
  date: z.string(), // 'YYYY-MM-DD', UTC day
  target: z.string(), // '30 cm Soil Temp'
  row_type: z.enum(['Historical', 'Forecast']),
  value: z.number().nullable(), // Historical only
  P10: z.number().nullable(), // Forecast only (coldest)
  P25: z.number().nullable(),
  P50: z.number().nullable(),
  P75: z.number().nullable(),
  P90: z.number().nullable(),
})

export const tundraForecastMeta = z.object({
  model_type: z.string().nullable(),
  version: z.string().nullable(),
  as_of: z.string().nullable(),
  site_id: z.number().nullable(),
  cable_serial: z.union([z.number(), z.string()]).nullable(),
  leg: z.enum(['cable-crossing', 'area']).nullable(),
  status: z.enum(['ok', 'degraded_area_only', 'unavailable_data']).nullable(),
  band_status: z.enum(['conformal', 'uncalibrated']).nullable(),
  data_freshness: z.string().nullable(),
  predicted_ready_date: z.object({
    p10: z.string().nullable(),
    p25: z.string().nullable(),
    p50: z.string().nullable(),
    p75: z.string().nullable(),
    p90: z.string().nullable(),
  }),
  expected_dnr_delay_days: z.number().nullable(),
  method: z.string().nullable(),
  band_note: z.string().nullable(),
  disclaimers: z.array(z.string()),
})

export const tundraForecastChart = z.object({
  rows: z.array(tundraForecastRow),
  meta: tundraForecastMeta,
})

export type TundraForecastRow = z.infer<typeof tundraForecastRow>
export type TundraForecastMeta = z.infer<typeof tundraForecastMeta>
export type TundraForecastChart = z.infer<typeof tundraForecastChart>
